// ============================================================
// VALLERJE — Painting Commission: the steps
//   0 Welcome · 1 Size · 2 Vision (theme + references)
//   3 Characters · 4 Details · 5 Review · 99 Confirmation
//
// Step numbers shown to the customer match the progress-bar
// circles, which count Welcome as 1 — so Size reads "Step 2 of 6".
// ============================================================

// ── Step 0: Welcome ─────────────────────────────────────────

function StepWelcome({ onNext }) {
  const cfg = vljCfg();
  const sizes = VLJ_SIZES();
  const cheapest = sizes.reduce((min, s) => (s.price < min.price ? s : min), sizes[0]);
  const points = (cfg.welcomePoints && cfg.welcomePoints.length ? cfg.welcomePoints : [
    "One-of-one originals", "Hand-painted in Sweden", "Worldwide shipping included"
  ]);

  return h("div", { style: { maxWidth: 720, margin: "0 auto", padding: "24px 0 20px", textAlign: "center" } },
    cfg.heroImage
      ? h("img", {
          src: cfg.heroImage,
          alt: "",
          style: { width: "100%", borderRadius: 14, border: "1px solid " + BORDER, display: "block", marginBottom: 40 }
        })
      : h("div", {
          "aria-hidden": "true",
          style: {
            width: "100%", aspectRatio: "16 / 9", borderRadius: 14,
            border: "1px solid " + BORDER, marginBottom: 40,
            background: "linear-gradient(135deg,#221d18 0%,#1a1612 55%,#2a241e 100%)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }
        }, h("span", {
          style: {
            fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, letterSpacing: "0.3em",
            color: "#3a3128", textTransform: "uppercase"
          }
        }, "Vallerje")),

    h("div", {
      style: {
        fontFamily: "'Bebas Neue',sans-serif", fontSize: "clamp(32px,5vw,56px)",
        lineHeight: 1.0, letterSpacing: "0.02em", color: TEXT, marginBottom: 24
      }
    },
      cfg.welcomeHeading || "Hey. I'm Valentino.", h("br", null),
      h("span", { style: { color: ACCENT } }, cfg.welcomeHeadingAccent || "Let's paint something you'll keep forever.")
    ),

    h("p", {
      style: {
        fontFamily: "'DM Sans',sans-serif", fontSize: 17, lineHeight: 1.75,
        color: TEXT_DIM, marginBottom: 32, maxWidth: 620, marginLeft: "auto", marginRight: "auto"
      }
    }, cfg.welcomeBody || ("Every painting I make is an original, built around your world — your characters, your scene, your wall. Answer a few questions and you'll see your exact price before you send anything. Takes about two minutes.")),

    h("button", {
      type: "button",
      className: "vlj-btn vlj-btn--hero",
      onClick: onNext
    }, "Start my commission →"),

    h("div", {
      style: { fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: TEXT_FAINT, marginTop: 16 }
    }, "Commissions from ", h("span", { style: { color: TEXT_DIM } }, price(cheapest.price)),
      " · shipping included · no payment today"),

    h("div", {
      style: { display: "flex", alignItems: "center", gap: 24, marginTop: 28, flexWrap: "wrap", justifyContent: "center" }
    }, points.map((s, i) =>
      h("div", { key: i, style: { display: "flex", alignItems: "center", gap: 8 } },
        h("span", { style: { color: ACCENT, fontSize: 8 } }, "✦"),
        h("span", { style: { fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: TEXT_FAINT } }, s)
      )
    ))
  );
}

// ── Step 1: Size ────────────────────────────────────────────

function SizeCard({ size, selected, onSelect }) {
  const [hovered, setHovered] = React.useState(false);
  return h("div", {
    onClick: () => onSelect(size.id),
    onKeyDown: e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(size.id); } },
    role: "button",
    tabIndex: 0,
    "aria-pressed": selected,
    style: {
      cursor: "pointer", borderRadius: 16, padding: "26px 22px 24px",
      border: "2px solid " + (selected ? ACCENT : hovered ? "#3a3835" : BORDER),
      background: selected ? "rgba(232,67,28,0.05)" : SURFACE,
      transition: "all 0.25s ease", position: "relative",
      display: "flex", flexDirection: "column",
      transform: hovered && !selected ? "translateY(-3px)" : "none"
    },
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false)
  },
    size.badge && h("div", {
      style: {
        position: "absolute", top: -10, right: 18,
        background: "linear-gradient(170deg,#ff8d6e 0%,#ff7356 55%,#d65a36 100%)",
        color: "#fffefc", border: "1px solid rgba(181,74,40,0.65)",
        fontFamily: "'DM Sans',sans-serif", fontSize: 10, fontWeight: 800,
        letterSpacing: "0.12em", textTransform: "uppercase", padding: "4px 10px", borderRadius: 999
      }
    }, size.badge),

    h("div", {
      style: { fontFamily: "'Bebas Neue',sans-serif", fontSize: 32, letterSpacing: "0.04em", color: TEXT, lineHeight: 1 }
    }, size.name),

    h("div", { style: { marginTop: 6, marginBottom: 14 } },
      h("div", {
        style: { fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: ACCENT, lineHeight: 1.45 }
      }, size.dimsCm),
      h("div", {
        style: { fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: TEXT_FAINT, lineHeight: 1.45 }
      }, size.dimsIn)
    ),

    h("p", {
      style: { fontFamily: "'DM Sans',sans-serif", fontSize: 13, lineHeight: 1.7, color: TEXT_DIM, margin: "0 0 20px", flex: 1 }
    }, size.blurb),

    h("div", { style: { marginTop: "auto" } },
      h("div", {
        style: { fontFamily: "'Bebas Neue',sans-serif", fontSize: 30, color: selected ? ACCENT : TEXT, lineHeight: 1 }
      }, price(size.price)),
      h("div", {
        style: { fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: TEXT_FAINT, marginTop: 4 }
      }, "1 character + shipping included")
    )
  );
}

function StepSize({ formData, updateForm, onNext, onBack }) {
  const sizes = VLJ_SIZES();
  const [guideOpen, setGuideOpen] = React.useState(false);

  function pick(id) {
    updateForm({ size: id });
  }

  return h("div", null,
    h(StepHeading, {
      eyebrow: "Step 2 of 6",
      title: "How big should it",
      accent: "be?",
      subtitle: "Each package is a size range, not one fixed canvas — the composition decides the final proportions and we agree them together. The price covers one character and worldwide shipping."
    }),

    h("button", {
      type: "button",
      onClick: () => setGuideOpen(true),
      style: {
        background: "none", border: "none", padding: 0, marginBottom: 22, cursor: "pointer",
        fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: ACCENT, textDecoration: "underline"
      }
    }, "Not sure? Open the size guide"),

    h("div", {
      className: "vlj-size-grid",
      style: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, alignItems: "stretch" }
    }, sizes.map(s => h(SizeCard, { key: s.id, size: s, selected: formData.size === s.id, onSelect: pick }))),

    h(SizeGuideModal, { isOpen: guideOpen, onClose: () => setGuideOpen(false) }),

    h(NavButtons, { onBack: onBack, onNext: onNext, nextDisabled: !formData.size })
  );
}

// ── Step 2: Characters ──────────────────────────────────────

function StepCharacters({ formData, updateForm, onNext, onBack }) {
  const pricing = VLJ_PRICING();
  const quote = buildQuote(formData);
  const atMax = formData.characters >= pricing.maxCharacters;

  function setCount(n) {
    const clamped = Math.min(pricing.maxCharacters, Math.max(1, n));
    updateForm({ characters: clamped });
  }

  const stepBtn = disabled => ({
    background: disabled ? "#1a1a18" : SURFACE,
    border: "1px solid " + (disabled ? "#1a1a18" : BORDER),
    borderRadius: 10,
    color: disabled ? "#3a3835" : TEXT,
    fontSize: 24, lineHeight: 1, cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.2s"
  });

  return h("div", null,
    h(StepHeading, {
      eyebrow: "Step 4 of 6",
      title: "How many characters",
      accent: "in the piece?",
      subtitle: "One character is already covered by your size package. Each additional character adds " + quote.perCharacterLabel + " to the price."
    }),

    h("div", {
      style: {
        background: SURFACE, border: "1px solid " + BORDER, borderRadius: 16,
        padding: "34px 24px", textAlign: "center"
      }
    },
      h("div", {
        style: { display: "flex", alignItems: "center", justifyContent: "center", gap: 24 }
      },
        h("button", {
          type: "button",
          className: "vlj-count-btn",
          onClick: () => setCount(formData.characters - 1),
          disabled: formData.characters <= 1,
          "aria-label": "One fewer character",
          style: stepBtn(formData.characters <= 1)
        }, "−"),

        h("div", { style: { minWidth: 96 } },
          h("div", {
            style: { fontFamily: "'Bebas Neue',sans-serif", fontSize: 72, lineHeight: 1, color: ACCENT }
          }, formData.characters),
          h("div", {
            style: {
              fontFamily: "'DM Sans',sans-serif", fontSize: 11, letterSpacing: "0.14em",
              textTransform: "uppercase", color: TEXT_FAINT, marginTop: 6
            }
          }, formData.characters === 1 ? "Character" : "Characters")
        ),

        h("button", {
          type: "button",
          className: "vlj-count-btn",
          onClick: () => setCount(formData.characters + 1),
          disabled: atMax,
          "aria-label": "One more character",
          style: stepBtn(atMax)
        }, "+")
      ),

      atMax && h("div", {
        style: {
          fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: TEXT_DIM,
          marginTop: 20, lineHeight: 1.7, maxWidth: 400, marginLeft: "auto", marginRight: "auto"
        }
      }, "That's the largest composition I quote automatically. Planning something bigger? Send this through anyway and mention it in the scene description — I'll quote it personally."),

      h("div", {
        style: {
          fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: TEXT_FAINT,
          marginTop: 16, lineHeight: 1.6
        }
      }, quote.includedCharacters + " included · each extra ", h("span", { style: { color: TEXT_DIM } }, "+" + quote.perCharacterLabel)),

      h("div", { style: { height: 1, background: BORDER, margin: "26px 0 20px" } }),

      h("div", { style: { maxWidth: 380, margin: "0 auto" } },
        h("div", {
          style: {
            fontFamily: "'DM Sans',sans-serif", fontSize: 11, letterSpacing: "0.14em",
            textTransform: "uppercase", color: TEXT_FAINT, marginBottom: 8
          }
        }, "Your price"),
        h("div", {
          style: { fontFamily: "'Bebas Neue',sans-serif", fontSize: 52, color: ACCENT, lineHeight: 1 }
        }, quote.totalLabel),
        h("div", {
          style: { fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: TEXT_FAINT, marginTop: 8 }
        }, "Worldwide shipping included")
      )
    ),

    h(NavButtons, { onBack: onBack, onNext: onNext })
  );
}

// ── Step 2: Vision — theme + references in one step ─────────

function ThemeSuggestions({ value, onPick }) {
  const filtered = ANIME_SUGGESTIONS.filter(s => !value || s.toLowerCase().includes(value.toLowerCase()));
  if (!filtered.length) return null;
  return h("div", {
    style: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }
  }, filtered.slice(0, 12).map(s =>
    h("button", {
      key: s,
      type: "button",
      onClick: () => onPick(s),
      style: {
        background: value === s ? "rgba(232,67,28,0.12)" : SURFACE,
        border: "1px solid " + (value === s ? ACCENT : BORDER),
        borderRadius: 999, padding: "7px 14px", cursor: "pointer",
        fontFamily: "'DM Sans',sans-serif", fontSize: 13,
        color: value === s ? ACCENT : TEXT_DIM, transition: "all 0.18s"
      },
      onMouseEnter: e => { if (value !== s) e.currentTarget.style.borderColor = "#3a3835"; },
      onMouseLeave: e => { if (value !== s) e.currentTarget.style.borderColor = BORDER; }
    }, s)
  ));
}

const MAX_REFERENCE_LINKS = 5;

function looksLikeUrl(value) {
  const v = String(value || "").trim();
  if (!v) return false;
  return /^(https?:\/\/|www\.)[^\s]+\.[^\s]+/i.test(v);
}

// A subhead that divides the two halves of this step without making it feel
// like two separate pages stitched together.
function VisionDivider({ label }) {
  return h("div", {
    style: { display: "flex", alignItems: "center", gap: 14, margin: "34px 0 22px" }
  },
    h("span", {
      style: {
        fontFamily: "'DM Sans',sans-serif", fontSize: 11, letterSpacing: "0.16em",
        textTransform: "uppercase", color: TEXT_FAINT, whiteSpace: "nowrap"
      }
    }, label),
    h("span", { style: { flex: 1, height: 1, background: BORDER } })
  );
}

function StepVision({ formData, updateForm, onNext, onBack }) {
  const links = formData.referenceLinks && formData.referenceLinks.length ? formData.referenceLinks : [""];
  const filledLinks = links.map(l => String(l || "").trim()).filter(Boolean);
  const anyInvalid = links.some(l => String(l || "").trim() && !looksLikeUrl(l));

  const themeOk = String(formData.theme || "").trim().length > 0;
  const briefOk = filledLinks.length > 0 || String(formData.sceneDescription || "").trim().length > 0;
  const canContinue = themeOk && briefOk && !anyInvalid;

  function setLink(i, val) {
    const next = links.slice();
    next[i] = val;
    updateForm({ referenceLinks: next });
  }
  function addLink() {
    if (links.length >= MAX_REFERENCE_LINKS) return;
    updateForm({ referenceLinks: links.concat([""]) });
  }
  function removeLink(i) {
    const next = links.filter((_, idx) => idx !== i);
    updateForm({ referenceLinks: next.length ? next : [""] });
  }

  return h("div", null,
    h(StepHeading, {
      eyebrow: "Step 3 of 6",
      title: "Show me what's",
      accent: "in your head.",
      subtitle: "The world we're working in, and anything that shows the direction. The more specific you are here, the closer my first sketch lands."
    }),

    h("div", { style: { maxWidth: 620 } },

      h(FormField, { label: "Anime or theme", required: true },
        h("input", {
          type: "text",
          value: formData.theme || "",
          onChange: e => updateForm({ theme: e.target.value }),
          placeholder: "e.g. Solo Leveling, Berserk, or your own concept",
          style: inputStyle(!!formData.theme),
          ...focusBind(() => !!formData.theme)
        }),
        h(ThemeSuggestions, { value: formData.theme || "", onPick: val => updateForm({ theme: val }) })
      ),

      h(VisionDivider, { label: "References" }),

      h(FormField, {
        label: "Reference links",
        hint: "Up to " + MAX_REFERENCE_LINKS + " links — a Pinterest board, a Google Drive folder, an Instagram post, a screenshot on Imgur. Make sure anything on Drive or Dropbox is set to “anyone with the link can view.”"
      },
        h("div", { style: { display: "flex", flexDirection: "column", gap: 10 } },
          links.map((link, i) => {
            const value = String(link || "");
            const invalid = value.trim() && !looksLikeUrl(value);
            return h("div", { key: i, style: { display: "flex", gap: 8, alignItems: "flex-start" } },
              h("div", { style: { flex: 1, minWidth: 0 } },
                h("input", {
                  type: "url",
                  inputMode: "url",
                  value: value,
                  onChange: e => setLink(i, e.target.value),
                  placeholder: i === 0 ? "https://pinterest.com/your-board" : "https://…",
                  className: "vlj-break",
                  style: {
                    ...inputStyle(!!value.trim()),
                    borderColor: invalid ? "#8a3a2a" : value.trim() ? "rgba(232,67,28,0.4)" : BORDER
                  },
                  ...focusBind(() => !!value.trim())
                }),
                invalid && h("div", {
                  style: { fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: "#c9603f", marginTop: 5 }
                }, "That doesn't look like a link — it should start with https://")
              ),
              links.length > 1 && h("button", {
                type: "button",
                onClick: () => removeLink(i),
                "aria-label": "Remove this link",
                style: {
                  background: "none", border: "1px solid " + BORDER, borderRadius: 8,
                  width: 44, height: 44, flexShrink: 0, color: TEXT_FAINT, cursor: "pointer", fontSize: 15
                }
              }, "×")
            );
          })
        ),

        links.length < MAX_REFERENCE_LINKS && h("button", {
          type: "button",
          onClick: addLink,
          style: {
            marginTop: 12, background: "none", border: "1px dashed " + BORDER, borderRadius: 8,
            padding: "11px 18px", cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
            fontSize: 13, color: TEXT_DIM, width: "100%", transition: "all 0.2s"
          },
          onMouseEnter: e => { e.currentTarget.style.borderColor = ACCENT; e.currentTarget.style.color = ACCENT; },
          onMouseLeave: e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.color = TEXT_DIM; }
        }, "+ Add another link")
      ),

      h(FormField, {
        label: "Describe the scene",
        hint: "Who is in it, what's happening, the mood, the colours you're imagining."
      },
        h("textarea", {
          value: formData.sceneDescription || "",
          onChange: e => updateForm({ sceneDescription: e.target.value }),
          rows: 5,
          placeholder: "e.g. Two characters back to back mid-fight, heavy rain, cold blue light with one red accent…",
          style: { ...inputStyle(!!formData.sceneDescription), resize: "vertical", lineHeight: 1.6 },
          ...focusBind(() => !!formData.sceneDescription)
        })
      ),

      h("div", {
        style: {
          background: "rgba(232,67,28,0.05)", border: "1px solid rgba(232,67,28,0.2)",
          borderRadius: 10, padding: "14px 16px",
          fontFamily: "'DM Sans',sans-serif", fontSize: 13, lineHeight: 1.7, color: TEXT_DIM
        }
      }, "Got images sitting on your phone instead? Send this through and reply to my email with them attached — that thread reaches me directly."),

      themeOk && !briefOk && h("div", {
        style: { fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: TEXT_FAINT, marginTop: 14 }
      }, "Add a reference link or describe the scene to continue."),

      anyInvalid && h("div", {
        style: { fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: "#c9603f", marginTop: 14 }
      }, "Fix or clear the highlighted link to continue.")
    ),

    h(NavButtons, { onBack: onBack, onNext: onNext, nextDisabled: !canContinue })
  );
}

// ── Step 5: Details ─────────────────────────────────────────

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || "").trim());
}

function StepDetails({ formData, updateForm, onNext, onBack }) {
  const nameOk = String(formData.name || "").trim().length > 1;
  const emailOk = isValidEmail(formData.email);
  const countryOk = !!formData.country;
  const canContinue = nameOk && emailOk && countryOk;
  const [touched, setTouched] = React.useState({});

  return h("div", null,
    h(StepHeading, {
      eyebrow: "Step 5 of 6",
      title: "Where do I",
      accent: "reach you?",
      subtitle: "This goes straight to me, not to a support queue. I read every brief personally and reply by email."
    }),

    h("div", { style: { maxWidth: 620 } },
      h("div", {
        className: "vlj-field-row",
        style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }
      },
        h(FormField, { label: "Your name", required: true },
          h("input", {
            type: "text",
            value: formData.name || "",
            onChange: e => updateForm({ name: e.target.value }),
            onBlur: () => setTouched(t => ({ ...t, name: true })),
            placeholder: "First and last",
            autoComplete: "name",
            style: {
              ...inputStyle(!!formData.name),
              borderColor: touched.name && !nameOk ? "#8a3a2a" : formData.name ? "rgba(232,67,28,0.4)" : BORDER
            }
          })
        ),
        h(FormField, { label: "Email", required: true },
          h("input", {
            type: "email",
            inputMode: "email",
            value: formData.email || "",
            onChange: e => updateForm({ email: e.target.value }),
            onBlur: () => setTouched(t => ({ ...t, email: true })),
            placeholder: "you@email.com",
            autoComplete: "email",
            style: {
              ...inputStyle(!!formData.email),
              borderColor: touched.email && !emailOk ? "#8a3a2a" : formData.email ? "rgba(232,67,28,0.4)" : BORDER
            }
          }),
          touched.email && !emailOk && h("div", {
            style: { fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: "#c9603f", marginTop: 6 }
          }, "Check that email — my reply goes there.")
        )
      ),

      h(FormField, { label: "Country", required: true, hint: "So I know where it's heading — shipping is already included in your price." },
        h("select", {
          value: formData.country || "",
          onChange: e => updateForm({ country: e.target.value }),
          style: inputStyle(!!formData.country)
        },
          h("option", { value: "" }, "Select your country"),
          COUNTRIES.map(c => h("option", { key: c, value: c }, c))
        )
      ),

      h(FormField, { label: "Social handle", hint: "Optional — helps me see what you're into." },
        h("div", { className: "vlj-social-row", style: { display: "flex" } },
          h("select", {
            value: formData.socialPlatform || "Instagram",
            onChange: e => updateForm({ socialPlatform: e.target.value }),
            style: {
              ...inputStyle(false), width: "auto", minWidth: 130, flexShrink: 0,
              borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: "none"
            }
          }, SOCIAL_PLATFORMS.map(p => h("option", { key: p, value: p }, p))),
          h("input", {
            type: "text",
            value: formData.socialHandle || "",
            onChange: e => updateForm({ socialHandle: e.target.value }),
            placeholder: "@yourhandle",
            style: {
              ...inputStyle(!!formData.socialHandle),
              borderTopLeftRadius: 0, borderBottomLeftRadius: 0
            }
          })
        )
      ),

      h("label", {
        style: {
          display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer",
          background: SURFACE, border: "1px solid " + BORDER, borderRadius: 10, padding: "14px 16px"
        }
      },
        h("input", {
          type: "checkbox",
          checked: !!formData.newsletter,
          onChange: e => updateForm({ newsletter: e.target.checked }),
          style: { marginTop: 3, accentColor: ACCENT, width: 16, height: 16, flexShrink: 0, cursor: "pointer" }
        }),
        h("span", {
          style: { fontFamily: "'DM Sans',sans-serif", fontSize: 13, lineHeight: 1.65, color: TEXT_DIM }
        }, "Send me studio updates, new originals and drops. Unsubscribe any time.",
          h("span", { style: { display: "block", color: TEXT_FAINT, fontSize: 12, marginTop: 3 } },
            "Optional — leaving this unticked won't affect your commission request.")
        )
      )
    ),

    h(NavButtons, { onBack: onBack, onNext: onNext, nextLabel: "See my price", nextDisabled: !canContinue })
  );
}

// ── Step 6: Review ──────────────────────────────────────────

function ReviewRow({ label, value, onEdit }) {
  if (!value) return null;
  return h("div", {
    className: "vlj-review-row",
    style: {
      display: "flex", gap: 16, alignItems: "flex-start",
      padding: "13px 0", borderBottom: "1px solid " + BORDER
    }
  },
    h("div", {
      style: {
        flex: "0 0 150px", fontFamily: "'DM Sans',sans-serif", fontSize: 11,
        letterSpacing: "0.1em", textTransform: "uppercase", color: TEXT_FAINT, paddingTop: 2
      }
    }, label),
    h("div", {
      className: "vlj-break",
      style: { flex: 1, minWidth: 0, fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: TEXT, lineHeight: 1.6, whiteSpace: "pre-wrap" }
    }, value),
    onEdit && h("button", {
      type: "button",
      onClick: onEdit,
      style: {
        background: "none", border: "none", padding: 0, cursor: "pointer", flexShrink: 0,
        fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: ACCENT, textDecoration: "underline"
      }
    }, "Edit")
  );
}

function StepReview({ formData, onBack, onSubmitted, goToStep }) {
  const quote = buildQuote(formData);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const links = (formData.referenceLinks || []).map(l => String(l || "").trim()).filter(Boolean);

  function submit() {
    setBusy(true);
    setError(null);
    submitCommissionLead(formData)
      .then(() => { setBusy(false); onSubmitted(); })
      .catch(err => {
        console.error("[Vallerje] commission lead failed:", err);
        setBusy(false);
        setError("Something went wrong sending your request. Try again — or email me directly and I'll pick it up from there.");
      });
  }

  return h("div", { style: { maxWidth: 760, margin: "0 auto" } },
    h(StepHeading, {
      eyebrow: "Step 6 of 6",
      title: "Here's your",
      accent: "commission.",
      subtitle: "This is the price for exactly what you've specified, shipping included. Send it over and I'll confirm it personally, usually within 24 hours."
    }),

    h("div", {
      style: {
        background: "linear-gradient(135deg,rgba(232,67,28,0.09),rgba(232,67,28,0.02))",
        border: "1px solid rgba(232,67,28,0.3)", borderRadius: 16, padding: "28px 26px", marginBottom: 28
      }
    },
      h("div", { style: { maxWidth: 420 } }, h(QuoteRows, { quote: quote, dim: "#a8a097" })),
      h("div", { style: { height: 1, background: "rgba(232,67,28,0.25)", margin: "18px 0" } }),
      h("div", {
        style: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }
      },
        h("div", null,
          h("div", {
            style: {
              fontFamily: "'DM Sans',sans-serif", fontSize: 11, letterSpacing: "0.14em",
              textTransform: "uppercase", color: TEXT_FAINT, marginBottom: 6
            }
          }, "Your price"),
          h("div", {
            style: { fontFamily: "'Bebas Neue',sans-serif", fontSize: "clamp(44px,8vw,64px)", color: ACCENT, lineHeight: 0.95 }
          }, quote.totalLabel)
        ),
        h("div", {
          style: {
            fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: TEXT_DIM,
            lineHeight: 1.7, maxWidth: 280, textAlign: "right"
          }
        }, "Worldwide shipping is already included in this price. No payment is taken today.")
      )
    ),

    h("div", { style: { marginBottom: 32 } },
      h(ReviewRow, { label: "Size", value: quote.size ? quote.size.name + " · " + quote.size.dimsCm : "", onEdit: () => goToStep(1) }),
      h(ReviewRow, { label: "Theme", value: formData.theme, onEdit: () => goToStep(2) }),
      h(ReviewRow, { label: "References", value: links.length ? links.join("\n") : "", onEdit: () => goToStep(2) }),
      h(ReviewRow, { label: "The scene", value: formData.sceneDescription, onEdit: () => goToStep(2) }),
      h(ReviewRow, { label: "Characters", value: String(quote.characters), onEdit: () => goToStep(3) }),
      h(ReviewRow, { label: "Name", value: formData.name, onEdit: () => goToStep(4) }),
      h(ReviewRow, { label: "Email", value: formData.email, onEdit: () => goToStep(4) }),
      h(ReviewRow, { label: "Ships to", value: formData.country, onEdit: () => goToStep(4) }),
      h(ReviewRow, {
        label: "Social",
        value: formData.socialHandle ? formData.socialPlatform + " " + formData.socialHandle : "",
        onEdit: () => goToStep(4)
      })
    ),

    error && h("div", {
      style: {
        background: "rgba(201,96,63,0.1)", border: "1px solid rgba(201,96,63,0.4)",
        borderRadius: 10, padding: "14px 16px", marginBottom: 20,
        fontFamily: "'DM Sans',sans-serif", fontSize: 13, lineHeight: 1.7, color: "#e0a08a"
      }
    }, error),

    h("button", {
      type: "button",
      className: "vlj-btn vlj-btn--block",
      onClick: submit,
      disabled: busy
    }, busy ? "Sending your request…" : "Send my commission request →"),

    h("div", {
      style: { textAlign: "center", fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: TEXT_FAINT, marginTop: 14 }
    }, "No payment now. I confirm the price and timeline by email before anything is agreed."),

    h("div", { style: { marginTop: 24 } },
      h("button", {
        type: "button",
        className: "vlj-btn-secondary",
        onClick: onBack
      }, "← Back")
    )
  );
}

// ── Confirmation ────────────────────────────────────────────

function ConfirmationScreen({ formData }) {
  const quote = buildQuote(formData);
  const nextSteps = [
    { n: "01", text: "I read your brief personally — usually the same day" },
    { n: "02", text: "You get an email from me confirming the price, the timeline and anything I want to ask about the scene" },
    { n: "03", text: "I send a sketch for your approval before any paint touches canvas" },
    { n: "04", text: "You get progress photos at every stage until it ships to you, tracked and insured" }
  ];

  return h("div", { style: { maxWidth: 680, margin: "0 auto", padding: "48px 0 60px", textAlign: "center" } },
    h("div", {
      style: {
        width: 72, height: 72, borderRadius: "50%", background: "rgba(232,67,28,0.1)",
        border: "2px solid rgba(232,67,28,0.3)", display: "flex",
        alignItems: "center", justifyContent: "center", margin: "0 auto 28px"
      }
    },
      h("svg", { width: "28", height: "28", viewBox: "0 0 28 28", fill: "none" },
        h("polyline", { points: "5,14 11,20 23,8", stroke: ACCENT, strokeWidth: "2.5", strokeLinecap: "round", strokeLinejoin: "round" })
      )
    ),

    h("div", {
      style: {
        fontFamily: "'Bebas Neue',sans-serif", fontSize: "clamp(36px,7vw,72px)",
        letterSpacing: "0.02em", color: TEXT, lineHeight: 0.95, marginBottom: 16
      }
    }, "Got it.", h("br", null), h("span", { style: { color: ACCENT } }, "Let's paint this.")),

    h("p", {
      style: {
        fontFamily: "'DM Sans',sans-serif", fontSize: 16, color: TEXT_DIM,
        lineHeight: 1.75, maxWidth: 500, margin: "0 auto 36px"
      }
    }, "Your brief is with me. I'll reply to ",
      h("span", { style: { color: TEXT } }, formData.email),
      " within 24 hours to confirm everything."),

    quote.size && h("div", {
      style: {
        background: SURFACE, border: "1px solid " + BORDER, borderRadius: 12,
        padding: "20px 24px", marginBottom: 28, display: "flex",
        justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap", textAlign: "left"
      }
    },
      h("div", null,
        h("div", {
          style: { fontFamily: "'DM Sans',sans-serif", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: TEXT_FAINT, marginBottom: 5 }
        }, "Your commission"),
        h("div", {
          style: { fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: TEXT }
        }, quote.size.name, " · ", quote.size.dimsCm, " · ", quote.characters, " character", quote.characters > 1 ? "s" : "")
      ),
      h("div", { style: { textAlign: "right" } },
        h("div", {
          style: { fontFamily: "'Bebas Neue',sans-serif", fontSize: 32, color: ACCENT, lineHeight: 1 }
        }, quote.totalLabel),
        h("div", {
          style: { fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: TEXT_FAINT, marginTop: 4 }
        }, "shipping included")
      )
    ),

    h("div", {
      style: {
        textAlign: "left", background: SURFACE, border: "1px solid " + BORDER,
        borderRadius: 12, padding: "24px 28px", marginBottom: 28
      }
    },
      h("div", {
        style: { fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, letterSpacing: "0.06em", color: TEXT_DIM, marginBottom: 20 }
      }, "What happens next"),
      h("div", { style: { display: "flex", flexDirection: "column", gap: 18 } },
        nextSteps.map(s =>
          h("div", { key: s.n, style: { display: "flex", gap: 16, alignItems: "flex-start" } },
            h("div", {
              style: {
                flexShrink: 0, width: 32, height: 32, borderRadius: "50%",
                background: "rgba(232,67,28,0.1)", border: "1px solid rgba(232,67,28,0.25)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'Bebas Neue',sans-serif", fontSize: 14, color: ACCENT
              }
            }, s.n),
            h("div", {
              style: { fontFamily: "'DM Sans',sans-serif", fontSize: 14, lineHeight: 1.7, color: TEXT_DIM, paddingTop: 5 }
            }, s.text)
          )
        )
      )
    ),

    h("p", {
      style: { fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: TEXT_FAINT, lineHeight: 1.75, margin: "0 0 28px" }
    }, "Have reference images on your phone? Reply to my email with them attached and they'll land straight in your brief."),

    h("a", {
      href: "/",
      className: "vlj-btn-secondary",
      style: { textDecoration: "none" }
    }, "Back to the shop")
  );
}

Object.assign(window, {
  StepWelcome, StepSize, StepVision, StepCharacters,
  StepDetails, StepReview, ConfirmationScreen
});
